import React, { useEffect, useState } from 'react';
import useProducts from './hooks/useProducts';
import { Spinner, ErrorAlert } from '../../components/shared';
import ProductCard from './components/ProductCard';
import ProductDetail from './components/ProductDetail';

const SORT_OPTIONS = [
  { value: '', label: 'Mac dinh' },
  { value: 'nav_to_3_months', label: 'NAV 3 thang' },
  { value: 'nav_to_6_months', label: 'NAV 6 thang' },
  { value: 'nav_to_12_months', label: 'NAV 12 thang' },
  { value: 'nav_to_36_months', label: 'NAV 36 thang' },
];

function FilterTabs({ types, selected, onChange }) {
  const allTypes = [{ code: null, name: 'Tat ca' }, ...(types || [])];

  return (
    <div className="flex flex-wrap gap-2">
      {allTypes.map((t) => {
        const isActive = t.code === null
          ? selected.length === 0
          : selected.length === 1 && selected[0] === t.code;
        return (
          <button
            key={t.code || 'all'}
            type="button"
            onClick={() => onChange(t.code ? [t.code] : [])}
            className={
              isActive
                ? 'rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white'
                : 'rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-200'
            }
          >
            {t.name}
          </button>
        );
      })}
    </div>
  );
}

function SearchAndSort({ search, sort, sortOrder, onSearchChange, onSortChange }) {
  return (
    <div className="flex flex-wrap gap-3">
      <input
        type="text"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Tim theo ma quy..."
        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <select
        value={sort || ''}
        onChange={(e) => onSortChange(e.target.value || null)}
        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function Pagination({ page, totalItems, pageSize, onChange }) {
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / pageSize));
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-2 pt-4">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Truoc
      </button>
      <span className="text-sm text-gray-500">
        {page} / {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Sau
      </button>
    </div>
  );
}

export default function ProductsPage() {
  const {
    resources,
    products,
    loading,
    loadingDetail,
    error,
    filters,
    loadResources,
    loadProducts,
    getProductDetail,
    openBuyWebview,
    updateFilters,
    clearError,
  } = useProducts();

  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  useEffect(() => {
    loadProducts();
  }, [filters, loadProducts]);

  const items = products?.items || [];
  const totalItems = products?.totalItems || 0;

  return (
    <div className="space-y-4">
      <FilterTabs
        types={resources?.fundAssetTypes}
        selected={filters.type}
        onChange={(type) => updateFilters({ type })}
      />

      <SearchAndSort
        search={filters.search}
        sort={filters.sort}
        sortOrder={filters.sortOrder}
        onSearchChange={(search) => updateFilters({ search })}
        onSortChange={(sort) => updateFilters({ sort, sortOrder: sort ? 'DESC' : null })}
      />

      <ErrorAlert error={error} />

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500">
          Khong tim thay san pham nao
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {items.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={setSelectedProduct}
              />
            ))}
          </div>
          <Pagination
            page={filters.page}
            totalItems={totalItems}
            pageSize={filters.size}
            onChange={(page) => updateFilters({ page })}
          />
        </>
      )}

      {selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onLoadDetail={getProductDetail}
          onBuy={openBuyWebview}
          loadingDetail={loadingDetail}
        />
      )}
    </div>
  );
}
